/**
 * Project Requirements Service
 *
 * Centralized service defining all project requirements (fields, documents, etc.)
 * for different project types and stages.
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 * - What fields are required for intake form completion
 * - What documents are required for stage advancement
 * - What validations apply to different project types
 *
 * Used by:
 * - NewProjectFormTab.tsx (intake form completion %)
 * - documentValidationService.ts (stage advancement validation)
 * - Any future validation logic
 */

import { supabase } from '@/lib/supabaseClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ProjectType = 'Commercial' | 'Residential';
export type CommercialType = 'Front of Meter' | 'Back of Meter' | null;

export interface DocumentRequirement {
  /** Unique identifier for this requirement */
  id: string;

  /** Human-readable name */
  name: string;

  /** Document type in documents table */
  documentType: string;

  /** Description patterns to match (case-insensitive) */
  descriptionPatterns: string[];

  /** When to enforce this requirement */
  applicableWhen: (project: ProjectContext) => boolean;

  /** User-friendly error message when missing */
  errorMessage: string;
}

export interface FieldRequirement {
  /** Field name in form data or database */
  fieldName: string;

  /** Human-readable label */
  label: string;

  /** When to require this field */
  applicableWhen: (project: ProjectContext) => boolean;
}

export interface ProjectContext {
  projectType: ProjectType;
  commercialType?: CommercialType;
  pipelineId?: number;
  currentStage?: string;
}

export interface RequirementCheckResult {
  /** Whether all requirements are met */
  satisfied: boolean;

  /** List of missing items */
  missing: string[];

  /** Total number of requirements */
  total: number;

  /** Number of requirements met */
  met: number;

  /** Completion percentage (0-100) */
  percentage: number;
}

// ============================================================================
// DOCUMENT REQUIREMENTS CONFIGURATION
// ============================================================================

/**
 * Define all document requirements here.
 *
 * To add a new requirement:
 * 1. Add new DocumentRequirement object to this array
 * 2. Define when it applies using applicableWhen function
 * 3. Both intake form and stage advancement will automatically use it
 */
export const DOCUMENT_REQUIREMENTS: DocumentRequirement[] = [
  {
    id: 'power-bill-back-of-meter',
    name: 'Power Bill',
    documentType: 'invoice',
    descriptionPatterns: ['Power Bill', 'Utility Bill'],
    applicableWhen: (project) =>
      project.projectType === 'Commercial' &&
      project.commercialType === 'Back of Meter',
    errorMessage: 'Power bill required for Back of Meter projects'
  },

  // Future requirements can be added here:
  // {
  //   id: 'site-photos-residential',
  //   name: 'Site Photos',
  //   documentType: 'photo',
  //   descriptionPatterns: ['Site Photo', 'Property Photo'],
  //   applicableWhen: (project) => project.projectType === 'Residential',
  //   errorMessage: 'Site photos required for residential projects'
  // },
];

// ============================================================================
// FIELD REQUIREMENTS CONFIGURATION
// ============================================================================

/**
 * Define all field requirements here.
 *
 * These are used for intake form completion percentage calculation.
 */
export const FIELD_REQUIREMENTS: FieldRequirement[] = [
  // Common fields for all projects
  { fieldName: 'first_name', label: 'First Name', applicableWhen: () => true },
  { fieldName: 'last_name', label: 'Last Name', applicableWhen: () => true },
  { fieldName: 'contact_email', label: 'Email', applicableWhen: () => true },
  { fieldName: 'contact_phone', label: 'Phone', applicableWhen: () => true },
  { fieldName: 'project_full_address', label: 'Address', applicableWhen: () => true },
  { fieldName: 'project_city', label: 'City', applicableWhen: () => true },
  { fieldName: 'project_state', label: 'State', applicableWhen: () => true },
  { fieldName: 'project_zip_code', label: 'Zip Code', applicableWhen: () => true },
  { fieldName: 'system_type', label: 'System Type', applicableWhen: () => true },
  { fieldName: 'mounting_type', label: 'Mounting Type', applicableWhen: () => true },

  // Commercial-only fields
  {
    fieldName: 'company_name',
    label: 'Company Name',
    applicableWhen: (p) => p.projectType === 'Commercial'
  },
  {
    fieldName: 'entity_type',
    label: 'Entity Type',
    applicableWhen: (p) => p.projectType === 'Commercial'
  },
  {
    fieldName: 'utility_provider',
    label: 'Utility Provider',
    applicableWhen: (p) => p.projectType === 'Commercial'
  },

  // Conditional fields
  {
    fieldName: 'tax_benefit_eligible',
    label: 'Tax Benefit Eligible',
    applicableWhen: (p) => p.projectType === 'Commercial' && p.pipelineId === 1
  },

  // Residential-only fields (if any)
  // Add here as needed
];

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Check which document requirements apply to a project and whether they're met.
 *
 * This is used by stage advancement validation to block projects with missing documents.
 *
 * @param projectId - Project UUID
 * @param context - Project context (type, commercial type, etc.)
 * @returns Validation result with missing documents
 */
export async function checkDocumentRequirements(
  projectId: string,
  context: ProjectContext
): Promise<RequirementCheckResult> {
  // Find applicable requirements
  const applicableRequirements = DOCUMENT_REQUIREMENTS.filter(req =>
    req.applicableWhen(context)
  );

  if (applicableRequirements.length === 0) {
    return {
      satisfied: true,
      missing: [],
      total: 0,
      met: 0,
      percentage: 100
    };
  }

  const missing: string[] = [];

  // Check each requirement
  for (const requirement of applicableRequirements) {
    try {
      const hasDocument = await checkDocumentExists(
        projectId,
        requirement.documentType,
        requirement.descriptionPatterns
      );

      if (!hasDocument) {
        missing.push(requirement.name);
      }
    } catch (error) {
      console.error(`Error checking requirement ${requirement.id}:`, error);
      // Fail open: if we can't check, assume it exists to prevent blocking users
    }
  }

  const met = applicableRequirements.length - missing.length;
  const percentage = applicableRequirements.length > 0
    ? Math.round((met / applicableRequirements.length) * 100)
    : 100;

  return {
    satisfied: missing.length === 0,
    missing,
    total: applicableRequirements.length,
    met,
    percentage
  };
}

/**
 * Check which field requirements apply to a project and whether they're met.
 *
 * This is used by intake form to calculate completion percentage.
 *
 * @param formData - Current form data
 * @param context - Project context (type, commercial type, etc.)
 * @returns Validation result with missing fields
 */
export function checkFieldRequirements(
  formData: any,
  context: ProjectContext
): RequirementCheckResult {
  // Find applicable requirements
  const applicableRequirements = FIELD_REQUIREMENTS.filter(req =>
    req.applicableWhen(context)
  );

  const missing: string[] = [];

  // Check each requirement
  for (const requirement of applicableRequirements) {
    const value = formData[requirement.fieldName];
    const isFilled = Array.isArray(value)
      ? value.length > 0
      : value && value.toString().trim() !== '';

    if (!isFilled) {
      missing.push(requirement.label);
    }
  }

  const met = applicableRequirements.length - missing.length;
  const percentage = applicableRequirements.length > 0
    ? Math.round((met / applicableRequirements.length) * 100)
    : 100;

  return {
    satisfied: missing.length === 0,
    missing,
    total: applicableRequirements.length,
    met,
    percentage
  };
}

/**
 * Check overall project completion (fields + documents).
 *
 * This gives a holistic view of project readiness.
 *
 * @param projectId - Project UUID
 * @param formData - Current form data
 * @param context - Project context
 * @returns Combined validation result
 */
export async function checkOverallCompletion(
  projectId: string,
  formData: any,
  context: ProjectContext
): Promise<RequirementCheckResult> {
  const fieldCheck = checkFieldRequirements(formData, context);
  const documentCheck = await checkDocumentRequirements(projectId, context);

  const totalRequirements = fieldCheck.total + documentCheck.total;
  const totalMet = fieldCheck.met + documentCheck.met;
  const allMissing = [...fieldCheck.missing, ...documentCheck.missing];

  return {
    satisfied: allMissing.length === 0,
    missing: allMissing,
    total: totalRequirements,
    met: totalMet,
    percentage: totalRequirements > 0
      ? Math.round((totalMet / totalRequirements) * 100)
      : 100
  };
}

/**
 * Get user-friendly error messages for missing requirements.
 *
 * Used for toast notifications and validation messages.
 */
export function getRequirementErrorMessages(context: ProjectContext): string[] {
  return DOCUMENT_REQUIREMENTS
    .filter(req => req.applicableWhen(context))
    .map(req => req.errorMessage);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a document exists in the database matching the criteria.
 *
 * @param projectId - Project UUID
 * @param documentType - Document type to search for
 * @param descriptionPatterns - Patterns to match in description (case-insensitive)
 * @returns True if document exists, false otherwise
 */
async function checkDocumentExists(
  projectId: string,
  documentType: string,
  descriptionPatterns: string[]
): Promise<boolean> {
  // Build OR query for description patterns
  const orConditions = descriptionPatterns
    .map(pattern => `description.ilike.%${pattern}%`)
    .join(',');

  const { data, error } = await supabase
    .from('documents')
    .select('id')
    .eq('project_id', projectId)
    .eq('document_type', documentType)
    .or(orConditions)
    .limit(1);

  if (error) {
    throw error;
  }

  return data && data.length > 0;
}

/**
 * Get list of all applicable document requirements for a project.
 *
 * Useful for displaying requirements to users.
 */
export function getApplicableDocumentRequirements(
  context: ProjectContext
): DocumentRequirement[] {
  return DOCUMENT_REQUIREMENTS.filter(req => req.applicableWhen(context));
}

/**
 * Get list of all applicable field requirements for a project.
 *
 * Useful for displaying requirements to users.
 */
export function getApplicableFieldRequirements(
  context: ProjectContext
): FieldRequirement[] {
  return FIELD_REQUIREMENTS.filter(req => req.applicableWhen(context));
}
